import { QueryAggregate } from '@powerportalspro/core';
import { useT } from '@powerportalspro/react';
import {
  GridToolbarItems,
  MainGrid,
  NavigateOpenRecordGridButton,
  PagingMode,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';
import { useMemo } from 'react';

// Every opportunity in the sample data — about four thousand — in one list with no pager
// (pagingMode={PagingMode.Virtualize}). The first 50 load with the page, and the next 50 load each
// time the end of the list comes into view; the footer counts how many of the matching rows are
// loaded so far. Searching, sorting or switching view starts again from the top.
//
// The totals row counts and adds up every opportunity the grid matches, not just the rows loaded
// so far.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.GridDemos.GridInfiniteScrollMainDemo';

// A view of the page's own, so the demo doesn't depend on one stored in Dataverse.
const OPPORTUNITIES_VIEW_ID = '7b9d1f3a-5c7e-4a9b-8d2f-6e8a0c2b4d61';

const OPPORTUNITIES_FETCH_XML = `<fetch>
  <entity name="opportunity">
    <attribute name="name" />
    <attribute name="customerid" />
    <attribute name="estimatedvalue" />
    <attribute name="closeprobability" />
    <attribute name="estimatedclosedate" />
    <attribute name="statecode" />
    <order attribute="estimatedclosedate" descending="true" />
  </entity>
</fetch>`;

export function GridInfiniteScrollMainSample() {
  const t = useT();
  const views = useMemo<ViewDefinition[]>(
    () => [
      {
        id: OPPORTUNITIES_VIEW_ID,
        tableName: 'opportunity',
        displayName: t(`${STRINGS_BASE}.view-opportunities`),
        fetchXml: OPPORTUNITIES_FETCH_XML,
        columns: [
          { columnName: 'name', width: 300, aggregates: [{ aggregate: QueryAggregate.Count }] },
          { columnName: 'customerid', width: 220 },
          { columnName: 'estimatedvalue', width: 160, aggregates: [{ aggregate: QueryAggregate.Sum, format: 'C0' }] },
          { columnName: 'closeprobability', width: 130, aggregates: [{ aggregate: QueryAggregate.Average, format: 'N0' }] },
          { columnName: 'estimatedclosedate', width: 150 },
          { columnName: 'statecode', width: 110 },
        ],
      },
    ],
    [t],
  );

  return (
    <MainGrid
      tableName="opportunity"
      minHeight="600px"
      maxHeight="600px"
      pagingMode={PagingMode.Virtualize}
      pageSize={50}
      viewIds={[OPPORTUNITIES_VIEW_ID]}
      customViewDefinitions={views}
      defaultViewId={OPPORTUNITIES_VIEW_ID}
    >
      <GridToolbarItems>
        <NavigateOpenRecordGridButton urlFor={(record) => `demos/opportunity?opportunityId=${record.id}`} />
      </GridToolbarItems>
    </MainGrid>
  );
}
